// Client-side validation for the publisher onboarding form.
//
// Runs on submit and returns a field-keyed error map so the page can render
// each message next to its input and scroll to the first one. The server
// re-validates everything in sanitizeIntakePayload — this exists to tell the
// publisher what's wrong before a round trip, not to be the gate.

import type { IntakeValues } from '../../../types/publisherIntake';

export type IntakeErrors = Partial<Record<string, string>>;

// Matches the API's INTAKE_SLUG_RE and the DB CHECK: must start alphanumeric.
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;

// CTV-759 / ADR-0036: slugs that would be shadowed by a static route. `all`
// is the All-stations sentinel at /admin/all; the rest are the static
// top-level /admin segments in main.tsx, which outrank /admin/:slug — so a
// station slugged `onboard` has been unreachable all along. Mirrors
// RESERVED_STATION_SLUGS in selfservice-api/src/shared/validation.ts, which
// is the actual gate; this one just says so before the round trip.
const RESERVED_SLUGS = [
  'all',
  'onboard',
  'help-center',
  'publica-uploads',
  'publica-push-jobs',
  'waymark-credentials',
  // CTV-800: the staff Studio at /admin/studio, and the sentinel station slug
  // its commercials carry.
  'studio',
];
const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Purely guidance, shown beside the CPM field; never blocks submit. */
export const RECOMMENDED_MIN_CPM = 25;

export function validateIntakePayload(
  values: IntakeValues,
  slugTaken = false,
): IntakeErrors {
  // Insertion order is render order in the submit summary, so this follows
  // the page: station (name, then slug), brand, pricing, contact.
  const errors: IntakeErrors = {};

  if (!values.name.trim()) errors.name = 'Enter your station name.';

  const slug = values.slug.trim().toLowerCase();
  if (!slug) errors.slug = 'Choose a name for your page on Streamline.';
  else if (!SLUG_RE.test(slug)) {
    errors.slug = 'Use 2-64 lowercase letters, numbers, or dashes, starting with a letter or number.';
  } else if (RESERVED_SLUGS.includes(slug)) {
    errors.slug = 'That page name is reserved. Try another.';
  } else if (slugTaken) {
    errors.slug = 'That page name is already taken. Try another.';
  }

  if (!values.logoUrl.trim()) errors.logoUrl = 'Upload your logo.';

  // Only the primary colour is required; it drives the channel's theme. The
  // API treats all three as optional (sanitizeIntakePayload maps '' to null),
  // and the swatches render black while holding '', so requiring secondary and
  // accent silently blocked publishers who never clicked them.
  if (!values.primaryColor.trim()) {
    errors.primaryColor = 'Pick your main brand color.';
  }
  for (const key of ['primaryColor', 'secondaryColor', 'accentColor'] as const) {
    const value = values[key].trim();
    if (value && !HEX_RE.test(value)) errors[key] = 'Use a hex color like #1B6FB8.';
  }

  const cpm = Number(values.ronCpm);
  if (!values.ronCpm.trim()) errors.ronCpm = 'Enter your CPM.';
  else if (!Number.isFinite(cpm) || cpm <= 0) errors.ronCpm = 'CPM must be greater than zero.';

  const adOpsEmail = values.adOpsEmail.trim().toLowerCase();
  if (!adOpsEmail) errors.adOpsEmail = 'Enter an ad ops or technical contact email.';
  else if (!EMAIL_RE.test(adOpsEmail)) errors.adOpsEmail = 'Enter a valid contact email.';
  // The input auto-formats to (XXX) XXX-XXXX and caps at 10 digits, so the
  // only invalid present state is a partially-typed number.
  const adOpsPhone = values.adOpsPhone.trim();
  if (adOpsPhone && adOpsPhone.replace(/\D/g, '').length !== 10) {
    errors.adOpsPhone = 'Enter a 10-digit phone number, or leave it blank.';
  }

  if (values.showTeamCommission && values.teamCommissionPercent.trim()) {
    const pct = Number(values.teamCommissionPercent);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      errors.teamCommissionPercent = 'Commission % must be between 0 and 100, or left blank.';
    }
  }

  // The team is optional — a high-level org contact filling this in for
  // several stations may not know each station's roster. Rows that DO exist
  // must still be complete and valid.
  const seen = new Set<string>();
  values.teamMembers.forEach((m, i) => {
    // A row that was added and then abandoned (nothing typed) is dropped at
    // submit, so it must not block either. Half-filled rows still validate.
    if (!m.name.trim() && !m.email.trim()) return;
    if (!m.name.trim()) errors[`teamMembers.${i}.name`] = 'Enter a name.';
    const email = m.email.trim().toLowerCase();
    if (!email) errors[`teamMembers.${i}.email`] = 'Enter an email.';
    else if (!EMAIL_RE.test(email)) errors[`teamMembers.${i}.email`] = 'Enter a valid email.';
    else if (seen.has(email)) errors[`teamMembers.${i}.email`] = 'This email is listed twice.';
    else seen.add(email);
  });

  return errors;
}

/** The wire payload, derived from the form's string-typed inputs. */
export function toIntakePayload(values: IntakeValues) {
  return {
    slug: values.slug.trim().toLowerCase(),
    name: values.name.trim(),
    logoUrl: values.logoUrl.trim(),
    logoInvert: values.logoInvert,
    logoScale: values.logoScale,
    primaryColor: values.primaryColor.trim(),
    secondaryColor: values.secondaryColor.trim(),
    accentColor: values.accentColor.trim(),
    ronCpm: Number(values.ronCpm),
    backgroundClips: values.backgroundClips,
    teamMembers: values.teamMembers
      // Abandoned empty rows never reach the wire.
      .filter((m) => m.name.trim() || m.email.trim())
      .map((m) => ({
        name: m.name.trim(),
        email: m.email.trim().toLowerCase(),
        role: m.role,
      })),
    adOpsContact: {
      email: values.adOpsEmail.trim().toLowerCase(),
      phone: values.adOpsPhone.trim() || null,
    },
    teamCommission: {
      enabled: values.showTeamCommission,
      defaultPercent:
        values.showTeamCommission && values.teamCommissionPercent.trim()
          ? Number(values.teamCommissionPercent)
          : null,
    },
    // displayName / markets / minBudget / minCampaignDays are deliberately
    // absent: form v2 no longer collects them, and resubmitting an old draft
    // hands those decisions back to the admin at finalize.
  };
}

/**
 * Format a US phone number as it is typed: digits only, capped at 10,
 * rendered (XXX) XXX-XXXX. Reformats from the digit string on every change,
 * so deleting through the punctuation behaves.
 */
export function formatUsPhone(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 10);
  if (d.length === 0) return '';
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function emptyIntakeValues(): IntakeValues {
  return {
    slug: '',
    name: '',
    logoUrl: '',
    logoInvert: false,
    logoScale: 1,
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    ronCpm: '',
    backgroundClips: [],
    teamMembers: [],
    adOpsEmail: '',
    adOpsPhone: '',
    showTeamCommission: false,
    teamCommissionPercent: '',
  };
}
